import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, Button, Row, Col, Input, List, Divider, Tooltip, message, Spin, Modal, Form, InputNumber } from 'antd';
import { PlusOutlined, EnvironmentOutlined, DeleteOutlined, SwapOutlined, RightOutlined, InfoCircleOutlined, LineChartOutlined } from '@ant-design/icons';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { request } from '../../utils/request';
import './RouteAnalysis.css';
import ElevationChart from '../../components/ElevationChart';
import { fetchElevationData, calculateTotalDistance, calculateDistance, sampleCoordinatesByDistance, interpolatePoints } from '../../utils/ElevationUtils';

// Mapbox访问令牌 - 从环境变量获取
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const RouteAnalysis = () => {
  // 状态管理
  const [map, setMap] = useState(null);
  const [waypoints, setWaypoints] = useState([]);
  const [route, setRoute] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [routeStats, setRouteStats] = useState(null);
  const [isBikeSpeedModalVisible, setBikeSpeedModalVisible] = useState(false);
  const [bikeSpeedForm] = Form.useForm();
  const [bikeSpeedResult, setBikeSpeedResult] = useState(null);
  const [elevationData, setElevationData] = useState([]);
  const [loadingElevation, setLoadingElevation] = useState(false);
  const [elevationStatus, setElevationStatus] = useState('');
  const [mapReady, setMapReady] = useState(false);
  const [allowMapDrag, setAllowMapDrag] = useState(true);
  const [selectedPoint, setSelectedPoint] = useState(null);
  
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const routeLayerRef = useRef(null);
  const selectedPointMarkerRef = useRef(null);
  
  // 初始化地图
  const initializeMap = useCallback(() => {
    console.log('初始化地图...');
    if (mapRef.current) {
      console.log('地图已存在，不重复初始化');
      return; // 防止重复初始化
    }

    if (!mapboxgl.supported()) {
      message.error('您的浏览器不支持Mapbox GL');
      return;
    }

    try {
      mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN || '';
      
      // 创建地图实例
      const mapInstance = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: 'mapbox://styles/mapbox/outdoors-v12', // 使用户外样式，更适合路线规划
        center: [116.397, 39.908], // 默认中心点（北京）
        zoom: 11,
        pitchWithRotate: true,
        hash: true // 启用URL哈希，保留地图状态
      });
      
      // 保存地图实例引用
      mapRef.current = mapInstance;
      setMap(mapInstance);
      
      // 添加导航控件
      mapInstance.addControl(new mapboxgl.NavigationControl(), 'top-right');
      
      // 添加比例尺
      mapInstance.addControl(new mapboxgl.ScaleControl({
        maxWidth: 100,
        unit: 'metric'
      }), 'bottom-left');
      
      // 添加定位控件
      mapInstance.addControl(new mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true
        },
        trackUserLocation: true,
        showUserHeading: true
      }), 'top-right');
      
      console.log('地图控件添加成功');
      
      // 地图加载完成事件
      mapInstance.on('load', () => {
        console.log('地图加载完成');
        
        try {
          // 加载地形数据（用于海拔查询）
          console.log('正在加载地形数据...');
          mapInstance.addSource('mapbox-dem', {
            'type': 'raster-dem',
            'url': 'mapbox://mapbox.mapbox-terrain-dem-v1',
            'tileSize': 512,
            'maxzoom': 14
          });
          
          // 设置地形渲染
          mapInstance.setTerrain({ 'source': 'mapbox-dem', 'exaggeration': 1 });
          console.log('地形数据加载成功');
          
          // 添加路线数据源
          mapInstance.addSource('route', {
            'type': 'geojson',
            'data': {
              'type': 'Feature',
              'properties': {},
              'geometry': {
                'type': 'LineString',
                'coordinates': []
              }
            }
          });
          
          // 添加路线图层
          mapInstance.addLayer({
            'id': 'route',
            'type': 'line',
            'source': 'route',
            'layout': {
              'line-join': 'round',
              'line-cap': 'round'
            },
            'paint': {
              'line-color': '#3887be',
              'line-width': 5,
              'line-opacity': 0.75
            }
          });
          
          // 保存路线图层引用
          routeLayerRef.current = mapInstance.getLayer('route');
          console.log('路线图层添加成功');
        } catch (error) {
          console.error('地形数据或路线图层加载失败:', error);
          message.error('地图资源加载失败，部分功能可能不可用');
        } finally {
          // 无论成功或失败，都设置地图为准备就绪状态
          setMapReady(true);
          console.log('地图准备就绪');
        }
      });
      
      // 地图错误事件
      mapInstance.on('error', (e) => {
        console.error('地图错误:', e);
        message.error(`地图错误: ${e.error?.message || '未知错误'}`);
      });
      
      // 设置路线拖动功能
      let isDraggingRoute = false;
      let routeDragStartPoint = null;
      
      // 路线鼠标悬停时改变光标样式
      mapInstance.on('mouseenter', 'route', () => {
        if (mapReady) {
          mapInstance.getCanvas().style.cursor = 'pointer';
        }
      });
      
      // 移出路线时恢复默认光标
      mapInstance.on('mouseleave', 'route', () => {
        if (mapReady) {
          mapInstance.getCanvas().style.cursor = '';
        }
      });
      
      // 路线拖动开始
      mapInstance.on('mousedown', 'route', (e) => {
        e.preventDefault();
        
        if (mapReady) {
          console.log('开始拖动路线');
          // 禁止地图拖动以避免冲突
          setAllowMapDrag(false);
          isDraggingRoute = true;
          routeDragStartPoint = e.lngLat;
          
          // 更改鼠标样式
          mapInstance.getCanvas().style.cursor = 'grab';
        }
      });
      
      // 处理鼠标移动
      mapInstance.on('mousemove', (e) => {
        if (isDraggingRoute && routeDragStartPoint) {
          // 更改鼠标样式
          mapInstance.getCanvas().style.cursor = 'grabbing';
        }
      });
      
      // 路线拖动结束
      mapInstance.on('mouseup', (e) => {
        if (isDraggingRoute && routeDragStartPoint) {
          console.log('结束拖动路线');
          // 在拖动结束位置添加新的途经点
          const newWaypoint = [e.lngLat.lng, e.lngLat.lat];
          
          // 寻找距离拖动点最近的路线段，在适当位置插入新途经点
          if (waypoints.length >= 2) {
            let minDistance = Infinity;
            let insertIndex = 1; // 默认在第一个点后插入
            
            for (let i = 0; i < waypoints.length - 1; i++) {
              // 计算拖动点到当前路线段的距离
              const dist = calculatePointToLineDistance(
                newWaypoint, 
                waypoints[i], 
                waypoints[i + 1]
              );
              
              if (dist < minDistance) {
                minDistance = dist;
                insertIndex = i + 1;
              }
            }
            
            // 在最近的路线段后添加新的途经点
            setWaypoints(prevWaypoints => {
              const newWaypoints = [...prevWaypoints];
              newWaypoints.splice(insertIndex, 0, newWaypoint);
              return newWaypoints;
            });
            
            // 重新规划路线
            setTimeout(() => {
              planRoute();
            }, 100);
          }
        }
        
        // 重置路线拖动状态
        isDraggingRoute = false;
        routeDragStartPoint = null;
        setAllowMapDrag(true);
        mapInstance.getCanvas().style.cursor = '';
      });
    } catch (error) {
      console.error('地图初始化失败:', error);
      message.error('地图初始化失败，请刷新页面重试');
    }
  }, []);
  
  // 组件挂载时初始化地图
  useEffect(() => {
    console.log('组件挂载，初始化地图');
    initializeMap();
    
    // 组件卸载时清理地图
    return () => {
      console.log('组件卸载，清理地图');
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [initializeMap]);
  
  // 设置地图点击事件 - 单独的 useEffect 以确保能访问最新的 waypoints 状态
  useEffect(() => {
    if (!mapRef.current || !mapReady) return;
    
    try {
      // 创建点击处理函数
      const handleMapClick = (e) => {
        // 使用函数式更新确保获取最新的 waypoints 状态
        setWaypoints(currentWaypoints => {
          if (currentWaypoints.length < 12) { // 修正最大路点数量为12
            return [...currentWaypoints, [e.lngLat.lng, e.lngLat.lat]];
          } else {
            message.warning('最多只能添加12个路点');
            return currentWaypoints;
          }
        });
      };
      
      // 添加点击事件
      mapRef.current.on('click', handleMapClick);
      
      // 清理函数
      return () => {
        if (mapRef.current) {
          try {
            mapRef.current.off('click', handleMapClick);
          } catch (error) {
            console.error('移除地图点击事件失败:', error);
          }
        }
      };
    } catch (error) {
      console.error('设置地图点击事件失败:', error);
      return () => {};
    }
  }, [mapReady]);
  
  // 更新路点标记
  useEffect(() => {
    if (!mapRef.current || !mapReady) return;
    
    try {
      // 清除现有标记
      markersRef.current.forEach(marker => {
        if (marker) {
          try {
            marker.remove();
          } catch (error) {
            console.error('移除标记失败:', error);
          }
        }
      });
      markersRef.current = [];
      
      // 添加新标记
      waypoints.forEach((waypoint, index) => {
        try {
          const el = document.createElement('div');
          el.className = 'waypoint-marker';
          el.innerHTML = `<span>${index + 1}</span>`;
          
          const marker = new mapboxgl.Marker({
            element: el,
            draggable: true
          })
            .setLngLat(waypoint)
            .addTo(mapRef.current);
            
          // 保存标记引用
          markersRef.current.push(marker);
          
          // 添加拖拽结束事件
          marker.on('dragend', () => {
            const lngLat = marker.getLngLat();
            setWaypoints(currentWaypoints => {
              const newWaypoints = [...currentWaypoints];
              newWaypoints[index] = [lngLat.lng, lngLat.lat];
              return newWaypoints;
            });
          });
        } catch (error) {
          console.error(`添加标记 ${index + 1} 失败:`, error);
        }
      });
    } catch (error) {
      console.error('更新路点标记失败:', error);
    }
  }, [waypoints, mapReady]);
  
  // 添加路点
  const addWaypoint = (coord) => {
    if (waypoints.length < 100) {
      setWaypoints([...waypoints, coord]);
    } else {
      message.warning('最多只能添加100个路点');
    }
  };
  
  // 删除路点
  const removeWaypoint = (index) => {
    const newWaypoints = [...waypoints];
    newWaypoints.splice(index, 1);
    setWaypoints(newWaypoints);
  };
  
  // 清除所有路点
  const clearAllWaypoints = () => {
    setWaypoints([]);
    setRoute(null);
    setRouteStats(null);
    setElevationData([]);
    message.success('已清除所有路点');
  };
  
  // 处理图表上的点选择
  const handlePointSelect = (point) => {
    // 如果没有点数据，并且不是因为鼠标离开图表（而是初始状态），则直接返回
    if (!point) {
      return;
    }
    
    if (!point.lng || !point.lat) {
      console.error("无效的点数据 - 缺少经纬度:", point);
      return;
    }
    
    // 确保经纬度为数字
    const lng = typeof point.lng === 'string' ? parseFloat(point.lng) : point.lng;
    const lat = typeof point.lat === 'string' ? parseFloat(point.lat) : point.lat;
    
    if (isNaN(lng) || isNaN(lat)) {
      console.error("无效的经纬度值:", { lng: point.lng, lat: point.lat });
      return;
    }
    
    // 确认坐标范围是否合理
    if (lng < -180 || lng > 180 || lat < -90 || lat > 90) {
      console.error("经纬度超出范围:", { lng, lat });
      return;
    }
    
    // 在地图上显示选中的点
    if (mapRef.current && mapReady) {
      try {
        // 使用GeoJSON点创建或更新图层 - 这种方法不会造成闪烁
        try {
          // 检查是否已存在数据源
          if (!mapRef.current.getSource('selected-point')) {
            // 第一次创建数据源和图层
            mapRef.current.addSource('selected-point', {
              type: 'geojson',
              data: {
                type: 'Feature',
                geometry: {
                  type: 'Point',
                  coordinates: [lng, lat]
                },
                properties: {}
              }
            });
            
            // 添加图层
            mapRef.current.addLayer({
              id: 'selected-point',
              type: 'circle',
              source: 'selected-point',
              paint: {
                'circle-radius': 10,
                'circle-color': '#3887be',
                'circle-stroke-width': 3,
                'circle-stroke-color': '#ffffff',
                'circle-opacity': 0.8
              }
            });
          } else {
            // 更新已有数据源的位置，无需重新创建图层
            mapRef.current.getSource('selected-point').setData({
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [lng, lat]
              },
              properties: {}
            });
          }
          
          // 将地图中心设置到选中的点
          mapRef.current.flyTo({
            center: [lng, lat],
            zoom: mapRef.current.getZoom(),
            essential: true, // 这个设置可以保证平滑过渡
            duration: 500  // 缩短动画时间
          });
          
          // 设置选中的点
          setSelectedPoint({...point, lng, lat});
        } catch (error) {
          console.error("添加/更新点图层失败:", error);
        }
      } catch (error) {
        console.error('处理点选择失败:', error);
      }
    }
  };
  
  // 交换路点顺序
  const swapWaypoints = (index) => {
    if (index < waypoints.length - 1) {
      const newWaypoints = [...waypoints];
      [newWaypoints[index], newWaypoints[index + 1]] = [newWaypoints[index + 1], newWaypoints[index]];
      setWaypoints(newWaypoints);
    }
  };
  
  // 规划路线
  const planRoute = async () => {
    if (waypoints.length < 2) {
      message.warning('请至少添加两个路点');
      return;
    }
    
    if (!mapRef.current || !mapReady) {
      message.error('地图尚未准备好，请稍后再试');
      return;
    }
    
    setIsLoading(true);
    setRoute(null);
    setRouteStats(null);
    setElevationData([]);
    
    try {
      console.log('开始规划路线...');
      
      // 构建API请求参数
      const coordinates = waypoints.map(wp => wp.join(',')).join(';');
      const url = `https://api.mapbox.com/directions/v5/mapbox/cycling/${coordinates}?alternatives=false&geometries=geojson&overview=full&steps=false&access_token=${mapboxgl.accessToken}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`API请求失败: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.code !== 'Ok' || !data.routes || data.routes.length === 0) {
        throw new Error(data.message || '无法规划路线');
      }
      
      const route = data.routes[0];
      console.log('路线规划成功:', route);
      
      // 更新路线数据源
      if (mapRef.current && mapRef.current.getSource('route')) {
        try {
          mapRef.current.getSource('route').setData({
            'type': 'Feature',
            'properties': {},
            'geometry': route.geometry
          });
        } catch (error) {
          console.error('更新路线数据源失败:', error);
          message.error('显示路线失败，但路线数据已获取');
        }
        
        // 调整地图视角以显示整个路线
        const coordinates = route.geometry.coordinates;
        
        if (coordinates.length > 0) {
          const bounds = coordinates.reduce((bounds, coord) => {
            return bounds.extend(coord);
          }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));
          
          mapRef.current.fitBounds(bounds, {
            padding: 50,
            duration: 1000
          });
        }
      } else {
        console.error('路线数据源不存在');
      }
      
      // 计算累计爬升
      const elevationGain = calculateElevationGain(route);
      
      // 更新路线统计信息
      setRouteStats({
        distance: route.distance,
        duration: route.duration,
        elevationGain
      });
      
      // 获取海拔数据
      try {
        console.log('开始获取海拔数据，路线数据:', route);
        
        // 确保使用正确的路线坐标格式
        if (route && route.geometry && route.geometry.coordinates) {
          const routeCoords = route.geometry.coordinates;
          console.log('路线坐标数量:', routeCoords.length);
          console.log('坐标格式示例:', routeCoords.slice(0, 2));
          
          // 调用海拔数据获取函数
          await fetchElevationData(
            routeCoords,
            mapRef.current,
            setLoadingElevation,
            setElevationStatus,
            setElevationData
          );
        } else {
          console.error('路线数据格式不正确:', route);
          setElevationStatus('路线数据格式错误，无法获取海拔');
        }
      } catch (error) {
        console.error('获取海拔数据失败:', error);
        message.error('获取海拔数据失败，路线统计信息将不完整');
      }
      
      console.log('路线规划和海拔数据获取完成');
    } catch (error) {
      console.error('路线规划失败:', error);
      message.error(`路线规划失败: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  // 计算累计爬升
  const calculateElevationGain = (route) => {
    console.log('计算累计爬升:', route);
    
    // 如果已有海拔数据，则使用现有数据计算
    if (elevationData && elevationData.length > 0) {
      console.log('使用现有海拔数据计算');
      let elevationGain = 0;
      
      for (let i = 1; i < elevationData.length; i++) {
        const elevationDiff = elevationData[i].elevation - elevationData[i-1].elevation;
        if (elevationDiff > 0) {
          elevationGain += elevationDiff;
        }
      }
      
      return elevationGain;
    }
    
    // 如果没有海拔数据，返回预估值
    console.log('返回预估爬升量');
    // 简单估算值：距离的1%作为爬升量，骑行过程中平均爬升
    // 这仅是一个粗略估计，实际将在获取海拔数据后更新
    const estimatedGain = route.distance * 0.01;
    return Math.round(estimatedGain);
  };
  
  // 格式化持续时间
  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    return `${hours}小时${minutes}分钟`;
  };
  
  // 显示自行车速度计算器
  const showBikeSpeedCalculator = () => {
    setBikeSpeedModalVisible(true);
    
    // 获取默认参数
    fetchDefaultParams();
  };
  
  // 获取默认参数
  const fetchDefaultParams = async () => {
    try {
      // 设置表单默认值
      bikeSpeedForm.setFieldsValue({
        weight: 75,
        bikeWeight: 10,
        power: 200,
        cda: 0.4,
        crr: 0.005,
        elevation: routeStats?.elevationGain || 0,
        distance: routeStats?.distance || 0
      });
    } catch (error) {
      console.error('获取默认参数失败:', error);
    }
  };
  
  // 计算速度
  const calculateSpeed = async (values = null) => {
    try {
      const formValues = values || await bikeSpeedForm.validateFields();
      
      // 简单的速度计算模型
      // 这里使用一个简化的模型，实际应用中可能需要更复杂的计算
      const { power, weight, bikeWeight, cda, crr, elevation, distance } = formValues;
      
      // 计算总重量
      const totalWeight = weight + bikeWeight;
      
      // 基础速度计算 (简化模型)
      // 功率与速度的关系受多种因素影响，这里使用简化公式
      let baseSpeed = Math.pow(power / (totalWeight * 0.01), 0.33) * 3.6;
      
      // 考虑空气阻力
      baseSpeed = baseSpeed * (0.5 / cda);
      
      // 考虑滚动阻力
      baseSpeed = baseSpeed * (0.005 / crr);
      
      // 考虑爬升
      // 爬升率 = 爬升高度 / 距离
      const climbRate = elevation / (distance * 1000);
      
      // 爬升对速度的影响 (简化)
      const climbFactor = 1 - (climbRate * 10);
      
      // 最终速度
      let finalSpeed = baseSpeed * climbFactor;
      
      // 确保速度在合理范围内
      finalSpeed = Math.max(5, Math.min(finalSpeed, 60));
      
      // 计算时间
      const timeHours = distance / finalSpeed;
      const hours = Math.floor(timeHours);
      const minutes = Math.round((timeHours - hours) * 60);
      
      // 设置结果
      setBikeSpeedResult({
        speed: finalSpeed.toFixed(1),
        time: `${hours}小时${minutes}分钟`
      });
      
    } catch (error) {
      console.error('计算速度失败:', error);
      message.error('计算失败，请检查输入参数');
    }
  };
  
  // 计算骑行时间
  const calculateRideTime = async () => {
    if (!routeStats) {
      message.warning('请先规划路线');
      return;
    }
    
    try {
      bikeSpeedForm.validateFields().then(values => {
        calculateSpeed(values);
      });
    } catch (error) {
      console.error('表单验证失败:', error);
    }
  };
  
  // 渲染路点列表
  const renderWaypointsList = () => {
    return (
      <List
        size="small"
        bordered
        dataSource={waypoints}
        renderItem={(waypoint, index) => (
          <List.Item
            actions={[
              <Tooltip title="删除此路点" key="delete">
                <Button 
                  type="text" 
                  danger 
                  icon={<DeleteOutlined />} 
                  onClick={() => removeWaypoint(index)}
                />
              </Tooltip>,
              index < waypoints.length - 1 && (
                <Tooltip title="与下一个路点交换位置" key="swap">
                  <Button 
                    type="text" 
                    icon={<SwapOutlined />} 
                    onClick={() => swapWaypoints(index)}
                  />
                </Tooltip>
              )
            ]}
          >
            <div className="waypoint-item">
              <span className="waypoint-index">{index + 1}</span>
              <span className="waypoint-coords">
                {waypoint[1].toFixed(5)}, {waypoint[0].toFixed(5)}
              </span>
            </div>
          </List.Item>
        )}
        locale={{ emptyText: '点击地图添加路点' }}
      />
    );
  };
  
  // 渲染路线统计信息
  const renderRouteStats = () => {
    if (!routeStats) return null;
    
    // 格式化距离和持续时间
    const distanceFormatted = (routeStats.distance / 1000).toFixed(1); // 转换为千米
    const durationFormatted = formatDuration(routeStats.duration);
    const elevationGainFormatted = Math.round(routeStats.elevationGain);
    
    return (
      <div className="route-stats">
        <h3>路线信息</h3>
        <div className="stats-container">
          <div className="stat-item">
            <div className="stat-value">{distanceFormatted}</div>
            <div className="stat-label">总距离 (公里)</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">{durationFormatted}</div>
            <div className="stat-label">预计时间</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">{elevationGainFormatted}</div>
            <div className="stat-label">累计爬升 (米)</div>
          </div>
        </div>
        <Button 
          type="primary" 
          onClick={showBikeSpeedCalculator}
          block
          style={{ marginBottom: '10px' }}
        >
          速度计算器
        </Button>
        <Button 
          type="primary" 
          onClick={calculateRideTime}
          block
        >
          预计骑行时间
        </Button>
      </div>
    );
  };
  
  // 组件卸载时清理
  useEffect(() => {
    return () => {
      // 清理所有标记
      markersRef.current.forEach(marker => {
        if (marker) marker.remove();
      });
      
      // 清理选中点标记
      if (selectedPointMarkerRef.current) {
        selectedPointMarkerRef.current.remove();
      }
    };
  }, []);
  
  // 计算点到线段的距离（用于确定拖动路线时添加新途经点的位置）
  const calculatePointToLineDistance = (point, lineStart, lineEnd) => {
    // 将坐标转换为容易计算的格式
    const x = point[0];
    const y = point[1];
    const x1 = lineStart[0];
    const y1 = lineStart[1];
    const x2 = lineEnd[0];
    const y2 = lineEnd[1];
    
    // 线段长度的平方
    const lineLength2 = Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2);
    
    // 如果线段实际上是一个点，则直接返回点到该点的距离
    if (lineLength2 === 0) {
      return Math.sqrt(Math.pow(x - x1, 2) + Math.pow(y - y1, 2));
    }
    
    // 计算投影比例 t
    const t = Math.max(0, Math.min(1, ((x - x1) * (x2 - x1) + (y - y1) * (y2 - y1)) / lineLength2));
    
    // 计算投影点坐标
    const projX = x1 + t * (x2 - x1);
    const projY = y1 + t * (y2 - y1);
    
    // 返回点到投影点的距离
    return Math.sqrt(Math.pow(x - projX, 2) + Math.pow(y - projY, 2));
  };
  
  return (
    <div className="route-analysis-container">
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={24} md={16} lg={18} xl={18}>
          <Card 
            title="地图" 
            bordered={false} 
            className="map-card"
            extra={
              <Tooltip title="拖拽标记可调整路点位置">
                <InfoCircleOutlined />
              </Tooltip>
            }
          >
            <div 
              ref={el => { mapContainerRef.current = el; }} 
              className="map-container" 
            />
          </Card>
          
          <Card 
            title="海拔分析" 
            bordered={false} 
            className="elevation-card"
            style={{ marginTop: '16px' }}
            extra={
              <Tooltip title="显示路线的海拔、爬升和坡度信息">
                <LineChartOutlined />
              </Tooltip>
            }
          >
            <ElevationChart 
              elevationData={elevationData}
              loadingElevation={loadingElevation}
              elevationStatus={elevationStatus}
              onPointSelect={handlePointSelect}
            />
          </Card>
        </Col>
        <Col xs={24} sm={24} md={8} lg={6} xl={6}>
          <Card title="路线规划" bordered={false}>
            <div className="waypoints-container">
              {renderWaypointsList()}
            </div>
            <div className="route-actions">
              <Button 
                type="primary" 
                onClick={planRoute} 
                loading={isLoading}
                disabled={waypoints.length < 2}
                block
                style={{ marginBottom: '8px' }}
              >
                规划路线
              </Button>
              <Button 
                danger
                onClick={clearAllWaypoints} 
                disabled={waypoints.length === 0}
                block
              >
                清除所有路点
              </Button>
            </div>
            {renderRouteStats()}
          </Card>
        </Col>
      </Row>
      
      <Modal
        title="骑行速度计算器"
        open={isBikeSpeedModalVisible}
        onCancel={() => setBikeSpeedModalVisible(false)}
        footer={[
          <Button key="back" onClick={() => setBikeSpeedModalVisible(false)}>
            关闭
          </Button>,
          <Button key="submit" type="primary" onClick={calculateRideTime}>
            计算
          </Button>
        ]}
      >
        {/* 骑行速度计算器表单 */}
        <Form
          form={bikeSpeedForm}
          layout="vertical"
          initialValues={{
            weight: 75,
            bikeWeight: 10,
            power: 200,
            cda: 0.4,
            crr: 0.005,
            elevation: routeStats?.elevationGain || 0,
            distance: routeStats?.distance || 0
          }}
        >
          <Form.Item label="骑行者体重 (kg)" name="weight" rules={[{ required: true }]}>
            <InputNumber min={30} max={150} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="自行车重量 (kg)" name="bikeWeight" rules={[{ required: true }]}>
            <InputNumber min={5} max={30} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="平均功率 (W)" name="power" rules={[{ required: true }]}>
            <InputNumber min={50} max={500} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="空气阻力系数 (CdA)" name="cda" rules={[{ required: true }]}>
            <InputNumber min={0.2} max={0.8} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="滚动阻力系数 (Crr)" name="crr" rules={[{ required: true }]}>
            <InputNumber min={0.002} max={0.02} step={0.001} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="累计爬升 (m)" name="elevation" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} disabled />
          </Form.Item>
          <Form.Item label="距离 (km)" name="distance" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} disabled />
          </Form.Item>
        </Form>
        
        {bikeSpeedResult && (
          <div className="bike-speed-result">
            <Divider>计算结果</Divider>
            <p><strong>预计平均速度:</strong> {bikeSpeedResult.speed} km/h</p>
            <p><strong>预计骑行时间:</strong> {bikeSpeedResult.time}</p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default RouteAnalysis; 